import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import {
  PlannableUser,
  ResourceSkill
} from '../../../Models/resource-assignment.model';
import { ResourcePlanningService } from '../../../Services/resource-planning.service';

export interface AvailabilityDialogData {
  users: PlannableUser[];
  skills: ResourceSkill[];
  defaultDate: Date;
}

export interface AvailabilityResult {
  userId: string;
  start: Date;
  end: Date;
}

interface Row {
  user: PlannableUser;
  free: boolean;
  reason: string;
}

@Component({
  selector: 'app-availability-dialog',
  templateUrl: './availability-dialog.component.html',
  styleUrls: ['./availability-dialog.component.css']
})
export class AvailabilityDialogComponent implements OnInit {
  dateStr = '';
  fromTime = '08:00';
  toTime = '17:00';
  requiredSkillIds: number[] = [];

  loading = false;
  rows: Row[] = [];

  constructor(
    private dialogRef: MatDialogRef<AvailabilityDialogComponent, AvailabilityResult>,
    @Inject(MAT_DIALOG_DATA) public data: AvailabilityDialogData,
    private service: ResourcePlanningService
  ) { }

  ngOnInit(): void {
    const d = this.data.defaultDate || new Date();
    this.dateStr = `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
    this.search();
  }

  get freeRows(): Row[] {
    return this.rows.filter(r => r.free);
  }

  get busyRows(): Row[] {
    return this.rows.filter(r => !r.free);
  }

  private window(): { start: Date; end: Date } | null {
    if (!this.dateStr) {
      return null;
    }
    const [y, m, d] = this.dateStr.split('-').map(v => parseInt(v, 10));
    const [fh, fm] = this.fromTime.split(':').map(v => parseInt(v, 10));
    const [th, tm] = this.toTime.split(':').map(v => parseInt(v, 10));
    if ([y, m, d, fh, fm, th, tm].some(isNaN)) {
      return null;
    }
    const start = new Date(y, m - 1, d, fh, fm, 0);
    const end = new Date(y, m - 1, d, th, tm, 0);
    if (end <= start) {
      return null;
    }
    return { start, end };
  }

  search(): void {
    const win = this.window();
    if (!win) {
      this.rows = [];
      return;
    }
    this.loading = true;
    const dayStart = new Date(win.start.getFullYear(), win.start.getMonth(), win.start.getDate());
    const dayEnd = new Date(win.start.getFullYear(), win.start.getMonth(), win.start.getDate() + 1);

    forkJoin({
      assignments: this.service.getAssignments(dayStart, dayEnd),
      external: this.service.getExternalEvents(dayStart, dayEnd)
    }).subscribe({
      next: res => {
        const assignments = res.assignments || [];
        const external = res.external || [];
        const ws = win.start.getTime();
        const we = win.end.getTime();

        const qualified = this.data.users.filter(u => {
          if (this.requiredSkillIds.length === 0) {
            return true;
          }
          const has = (u.skills || []).map(s => s.id);
          return this.requiredSkillIds.every(id => has.includes(id));
        });

        this.rows = qualified.map(u => {
          const busyAssignment = assignments.some(a =>
            (a.assignedUserId === u.id || (a.participantUserIds || []).includes(u.id)) &&
            (new Date(a.start).getTime() - (a.travelMinutesBefore || 0) * 60000) < we &&
            (new Date(a.end).getTime() + (a.travelMinutesAfter || 0) * 60000) > ws);
          const busyExternal = external.some(e =>
            e.userId === u.id &&
            new Date(e.start).getTime() < we && new Date(e.end).getTime() > ws);

          let reason = 'frei';
          if (busyAssignment) { reason = 'bereits eingeplant'; }
          else if (busyExternal) { reason = 'im Kalender belegt'; }

          return { user: u, free: !busyAssignment && !busyExternal, reason };
        }).sort((a, b) => (a.free === b.free) ? a.user.name.localeCompare(b.user.name) : (a.free ? -1 : 1));

        this.loading = false;
      },
      error: () => { this.loading = false; this.rows = []; }
    });
  }

  plan(row: Row): void {
    const win = this.window();
    if (!win) {
      return;
    }
    this.dialogRef.close({ userId: row.user.id, start: win.start, end: win.end });
  }

  close(): void {
    this.dialogRef.close();
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}
